import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
  DEFAULT_VAR_EXIT_PRICE,
  DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
  defaultLadderBands,
  ladderSummary,
  normalizeDecimalInput,
} from '../../utils/sniperArm';
import {
  parseBulkPatch,
  type BulkLadderOverlay,
  type SniperBulkPatch,
} from '../../utils/sniperBulk';
import { SniperLadderPopover } from './SniperLadderPopover';

export type { SniperBulkPatch };

/**
 * Bulk-edit bar for the markets SELECTED within one event card. The operator
 * types only the params they want to change (blank = leave untouched) and hits
 * Apply to write them to ALL selected markets in one request. Enable/Disable act
 * on the whole selection too. Add a new bulk param here + in `SniperBulkPatch`
 * and the wiring below — the shape is intentionally extensible.
 *
 * Validation mirrors `SniperMarketRow` and the backend DTO: size and the
 * auto-NO leg size (`noSize`) >= 1, maxPrice, varExitPrice, the VAR-defense
 * floor and the auto-NO ceiling ∈ (0,1], gate 1..100 (→ 0..1). The four
 * VAR-defense/auto-NO switches are tri-state (— = untouched, On/Off = set). A
 * field left blank/untouched is simply omitted from the patch; Apply is
 * enabled as long as at least one typed field is valid AND none typed is
 * invalid. `noSize` blank always means "untouched" here — unlike the per-row
 * field, bulk has no "clear to inherit `size`" action in this first stage.
 */
export function SniperBulkBar({
  count,
  busy,
  onApplyParams,
  onSetEnabled,
  onClear,
}: {
  count: number;
  busy: boolean;
  /** Receives an `onApplied` callback — invoked ONLY once the write succeeds. */
  onApplyParams: (patch: SniperBulkPatch, onApplied: () => void) => void;
  onSetEnabled: (enabled: boolean) => void;
  onClear: () => void;
}) {
  const [size, setSize] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [varExitPrice, setVarExitPrice] = useState('');
  const [gate, setGate] = useState('');
  // Tri-state VAR-defense switches: '' = leave untouched, 'true'/'false' = set.
  const [varDefenseEnabled, setVarDefenseEnabled] = useState('');
  const [varPreventiveExitEnabled, setVarPreventiveExitEnabled] = useState('');
  const [varConfirmedFloorPrice, setVarConfirmedFloorPrice] = useState('');
  const [varRebuyEnabled, setVarRebuyEnabled] = useState('');
  // Auto-NO period-close tail: same tri-state + price-field pattern. `noSize`
  // stays "blank = untouched" like every other bulk field — clearing back to
  // "inherit `size`" is a per-row-only action for now (see `parseBulkPatch`).
  const [autoNoEnabled, setAutoNoEnabled] = useState('');
  const [noMaxPrice, setNoMaxPrice] = useState('');
  const [noSize, setNoSize] = useState('');
  // The ladder has no "blank string" state, so untouched is an explicit null.
  const [ladder, setLadder] = useState<BulkLadderOverlay | null>(null);

  const parsed = useMemo(
    () =>
      parseBulkPatch(
        size,
        maxPrice,
        varExitPrice,
        gate,
        varDefenseEnabled,
        varPreventiveExitEnabled,
        varConfirmedFloorPrice,
        varRebuyEnabled,
        autoNoEnabled,
        noMaxPrice,
        noSize,
        ladder,
      ),
    [
      size,
      maxPrice,
      varExitPrice,
      gate,
      varDefenseEnabled,
      varPreventiveExitEnabled,
      varConfirmedFloorPrice,
      varRebuyEnabled,
      autoNoEnabled,
      noMaxPrice,
      noSize,
      ladder,
    ],
  );

  // Enable/Disable writes ONLY `{enabled}`. With params typed but not applied,
  // that would arm markets at their OLD size/gate while the bar still shows the
  // new values as if they had been written — fail closed and make the operator
  // Apply or clear first.
  const hasDrafts =
    size !== '' ||
    maxPrice !== '' ||
    varExitPrice !== '' ||
    gate !== '' ||
    varDefenseEnabled !== '' ||
    varPreventiveExitEnabled !== '' ||
    varConfirmedFloorPrice !== '' ||
    varRebuyEnabled !== '' ||
    autoNoEnabled !== '' ||
    noMaxPrice !== '' ||
    noSize !== '' ||
    ladder !== null;

  const clearDrafts = () => {
    setSize('');
    setMaxPrice('');
    setVarExitPrice('');
    setGate('');
    setVarDefenseEnabled('');
    setVarPreventiveExitEnabled('');
    setVarConfirmedFloorPrice('');
    setVarRebuyEnabled('');
    setAutoNoEnabled('');
    setNoMaxPrice('');
    setNoSize('');
    setLadder(null);
  };

  const apply = () => {
    if (!parsed.canApply || busy) return;
    // Drafts clear ONLY once the write lands. Clearing optimistically would drop
    // `hasDrafts` on a FAILED apply, re-opening Enable over params that were
    // never written — the exact footgun the gate exists to close.
    onApplyParams(parsed.patch, clearDrafts);
  };

  const fieldSx = {
    flexShrink: 0,
    '& .MuiInputBase-input': { py: 0.35, px: 0.75, fontSize: 12 },
    '& .MuiInputLabel-root': { fontSize: 10.5 },
    '& .MuiInputLabel-shrink': { fontSize: 11 },
  };
  const selectSx = { ...fieldSx, width: 78 };
  const btnSx = { flexShrink: 0, minWidth: 0, fontSize: 12 };

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1,
        py: 0.5,
        borderColor: 'primary.main',
        bgcolor: 'rgba(33,150,243,0.06)',
      }}
    >
      <Stack
        direction="row"
        spacing={0.75}
        alignItems="center"
        flexWrap="nowrap"
        useFlexGap
        sx={{ overflowX: 'auto' }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap' }}
        >
          {count} selected
        </Typography>
        <Box sx={{ width: '1px', flexShrink: 0, height: 20, bgcolor: 'divider', mx: 0.25 }} />

        <TextField
          size="small"
          label="Size"
          value={size}
          onChange={(e) => setSize(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'bulk size' }}
          sx={{ width: 66, ...fieldSx }}
        />
        <TextField
          size="small"
          label="Max $"
          value={maxPrice}
          onChange={(e) => setMaxPrice(normalizeDecimalInput(e.target.value))}
          inputProps={{ inputMode: 'decimal', 'aria-label': 'bulk max price' }}
          sx={{ width: 66, ...fieldSx }}
        />
        <Tooltip title="VAR exit — precautionary sell posted the moment a VAR review starts (Phase 1), before the cancellation is confirmed">
          <TextField
            size="small"
            label="Exit $"
            value={varExitPrice}
            onChange={(e) =>
              setVarExitPrice(normalizeDecimalInput(e.target.value))
            }
            inputProps={{
              inputMode: 'decimal',
              'aria-label': 'bulk var exit price',
            }}
            sx={{ width: 66, ...fieldSx }}
          />
        </Tooltip>
        <Tooltip title="VAR floor — sell price once the goal cancellation is CONFIRMED (Phase 2, or any other revert)">
          <TextField
            size="small"
            label="Floor $"
            value={varConfirmedFloorPrice}
            onChange={(e) =>
              setVarConfirmedFloorPrice(normalizeDecimalInput(e.target.value))
            }
            inputProps={{
              inputMode: 'decimal',
              'aria-label': 'bulk var confirmed floor price',
            }}
            sx={{ width: 66, ...fieldSx }}
          />
        </Tooltip>
        <Tooltip title="Auto-NO period-close tail — worst per-contract price the NO buy may pay">
          <TextField
            size="small"
            label="No $"
            value={noMaxPrice}
            onChange={(e) => setNoMaxPrice(normalizeDecimalInput(e.target.value))}
            inputProps={{
              inputMode: 'decimal',
              'aria-label': 'bulk auto-no max price',
            }}
            sx={{ width: 66, ...fieldSx }}
          />
        </Tooltip>
        <Tooltip title="Auto-NO sweep size (contracts) — independent of the main Size. Blank leaves it untouched">
          <TextField
            size="small"
            label="No Size"
            value={noSize}
            onChange={(e) => setNoSize(normalizeDecimalInput(e.target.value))}
            inputProps={{
              inputMode: 'decimal',
              'aria-label': 'bulk auto-no size',
            }}
            sx={{ width: 66, ...fieldSx }}
          />
        </Tooltip>
        <BulkTriState
          label="Defense"
          tooltip="VAR defense master switch"
          ariaLabel="bulk var defense enabled"
          value={varDefenseEnabled}
          onChange={setVarDefenseEnabled}
          sx={selectSx}
        />
        <BulkTriState
          label="Prev"
          tooltip="Phase 1 — break-even preventive sell"
          ariaLabel="bulk var preventive exit enabled"
          value={varPreventiveExitEnabled}
          onChange={setVarPreventiveExitEnabled}
          sx={selectSx}
        />
        <BulkTriState
          label="Rebuy"
          tooltip="All-clear rebuy if the VAR confirms the goal"
          ariaLabel="bulk var rebuy enabled"
          value={varRebuyEnabled}
          onChange={setVarRebuyEnabled}
          sx={selectSx}
        />
        <BulkTriState
          label="Auto-NO"
          tooltip="Auto-NO period-close tail — buy the NO the instant the period ends without the Over crossing"
          ariaLabel="bulk auto-no enabled"
          value={autoNoEnabled}
          onChange={setAutoNoEnabled}
          sx={selectSx}
        />
        <TextField
          size="small"
          label="Gate %"
          value={gate}
          onChange={(e) => setGate(e.target.value.replace(/[^0-9]/g, ''))}
          inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': 'bulk gate percent' }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ '& .MuiTypography-root': { fontSize: 11 } }}>
                %
              </InputAdornment>
            ),
          }}
          sx={{ width: 80, ...fieldSx }}
        />
        <SniperLadderPopover
          showLegacyExit={false}
          value={{
            ladderEnabled: ladder?.ladderEnabled ?? false,
            ladderBands: ladder?.ladderBands ?? defaultLadderBands(),
            floorPrice: ladder?.floorPrice ?? null,
            varPreventiveExitOffset:
              ladder?.varPreventiveExitOffset ?? DEFAULT_VAR_PREVENTIVE_EXIT_OFFSET,
            // Inert here — the bar's own Exit $ / Floor $ fields own these.
            varExitPrice: DEFAULT_VAR_EXIT_PRICE,
            varConfirmedFloorPrice: DEFAULT_VAR_CONFIRMED_FLOOR_PRICE,
          }}
          onApply={(next) =>
            setLadder({
              ladderEnabled: next.ladderEnabled,
              ladderBands: next.ladderBands,
              floorPrice: next.floorPrice,
              varPreventiveExitOffset: next.varPreventiveExitOffset,
            })
          }
          disabled={busy}
          renderTrigger={(open) => (
            <Tooltip title="Confidence ladder — Kalshi only. Untouched unless you Apply inside.">
              <Button
                size="small"
                variant={ladder ? 'contained' : 'outlined'}
                onClick={open}
                disabled={busy}
                sx={{ ...btnSx, px: 1, whiteSpace: 'nowrap' }}
              >
                {ladder
                  ? ladder.ladderEnabled
                    ? ladderSummary(ladder.ladderBands, ladder.floorPrice)
                    : 'ladder off'
                  : 'Ladder —'}
              </Button>
            </Tooltip>
          )}
        />
        {/* Staged ladder edits are cleared with the rest of the drafts on a
            successful Apply; this X is the only way to un-stage one alone. */}
        {ladder && (
          <Tooltip title="Leave the ladder untouched">
            <IconButton
              size="small"
              onClick={() => setLadder(null)}
              disabled={busy}
              aria-label="clear bulk ladder"
              sx={{ p: 0.25, flexShrink: 0 }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        <Button
          size="small"
          variant="contained"
          onClick={apply}
          disabled={!parsed.canApply || busy}
          sx={{ ...btnSx, px: 1.25 }}
        >
          Apply
        </Button>

        <Box sx={{ width: '1px', flexShrink: 0, height: 20, bgcolor: 'divider', mx: 0.25 }} />
        <Tooltip
          title={
            hasDrafts
              ? 'Apply or clear the typed params first — Enable writes only the on/off flag'
              : 'Enable all selected markets'
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onSetEnabled(true)}
              disabled={busy || hasDrafts}
              sx={{ ...btnSx, px: 1 }}
            >
              Enable
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            hasDrafts
              ? 'Apply or clear the typed params first — Disable writes only the on/off flag'
              : 'Disable all selected markets'
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => onSetEnabled(false)}
              disabled={busy || hasDrafts}
              sx={{ ...btnSx, px: 1 }}
            >
              Disable
            </Button>
          </span>
        </Tooltip>

        <Box sx={{ flex: 1 }} />
        <Tooltip title="Clear selection">
          <span>
            <IconButton size="small" onClick={onClear} disabled={busy} aria-label="clear selection" sx={{ p: 0.5, flexShrink: 0 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

/**
 * One tri-state VAR-defense switch for the bulk bar. A native `<select>` (— /
 * On / Off) so blank stays a real "leave untouched" and the two set values map
 * to the `'true'`/`'false'` strings `parseBulkPatch` reads. Native (not a MUI
 * menu) keeps the option list flat + drivable.
 */
function BulkTriState({
  label,
  tooltip,
  ariaLabel,
  value,
  onChange,
  sx,
}: {
  label: string;
  tooltip: string;
  ariaLabel: string;
  value: string;
  onChange: (value: string) => void;
  sx: SxProps<Theme>;
}) {
  return (
    <Tooltip title={tooltip}>
      <TextField
        select
        size="small"
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        SelectProps={{ native: true, inputProps: { 'aria-label': ariaLabel } }}
        InputLabelProps={{ shrink: true }}
        sx={sx}
      >
        <option value="">—</option>
        <option value="true">On</option>
        <option value="false">Off</option>
      </TextField>
    </Tooltip>
  );
}
